import React from "react";
import {
    Cog,
    LineChart,
    TrendingUp,
    Users,
    GitMerge,
    Landmark,
    Compass,
    Microscope,
    Map,
    Rocket,
    Clock,
} from "lucide-react";

import { PageShell } from "../components/layout/PageShell";
import { Container } from "../components/zen/Container";
import { Section } from "../components/zen/Section";
import { Eyebrow } from "../components/zen/Eyebrow";
import { Heading } from "../components/zen/Heading";
import { Button } from "../components/zen/Button";
import { PageHero } from "../components/zen/PageHero";
import { CapabilityCard } from "../components/zen/CapabilityCard";
import { Timeline } from "../components/zen/Timeline";
import { ClosingCTA } from "../components/zen/ClosingCTA";
import {
    FormField,
    FormSelect,
    FormTextarea,
    FormSuccess,
    FormCard,
    useLeadSubmit,
} from "../components/zen/FormField";
import { FormSubmit } from "../components/zen/FormSubmit";

const PRACTICES = [
    {
        icon: Cog,
        title: "Operational Systems",
        description:
            "Build the systems and SOPs that scale beyond founder execution.",
    },
    {
        icon: LineChart,
        title: "Financial Operations",
        description:
            "Cash management, forecasting, and financial controls.",
    },
    {
        icon: TrendingUp,
        title: "Growth Strategy",
        description: "Channel expansion, pricing, and unit economics.",
    },
    {
        icon: Users,
        title: "Organizational Design",
        description:
            "Structure, hiring frameworks, and management cadence.",
    },
    {
        icon: GitMerge,
        title: "M&A Advisory",
        description: "Acquisition diligence and integration planning.",
    },
    {
        icon: Landmark,
        title: "Board & Governance",
        description:
            "Board readiness, reporting cadence, and stakeholder relationships.",
    },
];

const STEPS = [
    {
        number: "01",
        title: "Discovery",
        description:
            "60-minute scoping conversation to understand your situation.",
        icon: Compass,
    },
    {
        number: "02",
        title: "Diagnostic",
        description:
            "Two-week assessment of operations, financials, and team.",
        icon: Microscope,
    },
    {
        number: "03",
        title: "Roadmap",
        description:
            "Prioritized 90-day execution plan with clear ownership.",
        icon: Map,
    },
    {
        number: "04",
        title: "Execution",
        description: "Hands-on partnership through implementation.",
        icon: Rocket,
    },
];

const INDUSTRIES = [
    "Engineering & Design",
    "Real Estate",
    "Industrial Supply",
    "Technology",
    "Professional Services",
    "Consumer",
];

const FORM_FIELDS = [
    { name: "full_name", required: true, type: "text" },
    { name: "email", required: true, type: "email" },
    { name: "phone", required: false, type: "text" },
    { name: "company_name", required: true, type: "text" },
    { name: "company_size", required: true, type: "select" },
    { name: "area_of_interest", required: true, type: "select" },
    { name: "description", required: true, type: "text" },
];

export default function Consulting() {
    const form = useLeadSubmit({
        endpoint: "/leads/consulting",
        fields: FORM_FIELDS,
    });

    return (
        <PageShell>
            <div data-testid="page-consulting">
                <PageHero
                    eyebrow="Consulting"
                    title="Operational advisory for serious operators."
                    subtitle="Strategy, systems, and financial operations consulting for founders and management teams scaling beyond product-market fit."
                    data-testid="cn-hero"
                    logoSrc="/logos/zenme-z-mark.png"
                    logoAlt="ZenMe Group corporate mark"
                >
                    <Button
                        href="#schedule"
                        variant="primary"
                        size="lg"
                        data-testid="cn-cta-schedule"
                    >
                        Schedule a Conversation
                    </Button>
                    <Button
                        to="/contact"
                        variant="ghost"
                        size="lg"
                        data-testid="cn-cta-contact"
                    >
                        General Inquiry
                    </Button>
                </PageHero>

                {/* PRACTICE AREAS */}
                <Section className="border-t border-border-subtle">
                    <Container>
                        <div className="max-w-2xl">
                            <Eyebrow>Practice Areas</Eyebrow>
                            <Heading as="h2" className="mt-4">
                                Where we go deep.
                            </Heading>
                        </div>
                        <div className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
                            {PRACTICES.map((p, i) => (
                                <CapabilityCard
                                    key={p.title}
                                    {...p}
                                    data-testid={`cn-practice-${i}`}
                                />
                            ))}
                        </div>
                    </Container>
                </Section>

                {/* APPROACH TIMELINE */}
                <Section className="border-t border-border-subtle">
                    <Container>
                        <div className="max-w-2xl">
                            <Eyebrow>Engagement Approach</Eyebrow>
                            <Heading as="h2" className="mt-4">
                                Diagnostic depth, hands-on execution.
                            </Heading>
                        </div>
                        <Timeline steps={STEPS} data-testid="cn-timeline" />
                    </Container>
                </Section>

                {/* INDUSTRIES */}
                <Section className="border-y border-border-subtle bg-elevated">
                    <Container>
                        <Eyebrow>Industries Served</Eyebrow>
                        <div className="mt-8 grid grid-cols-2 gap-y-8 sm:grid-cols-3 lg:grid-cols-6">
                            {INDUSTRIES.map((i) => (
                                <div
                                    key={i}
                                    className="flex items-center gap-2.5 text-[13px] uppercase tracking-widest-2 text-text-muted"
                                >
                                    <Clock
                                        className="h-3.5 w-3.5 opacity-50"
                                        strokeWidth={1.5}
                                    />
                                    <span>{i}</span>
                                </div>
                            ))}
                        </div>
                    </Container>
                </Section>

                {/* FORM */}
                <Section id="schedule" className="scroll-mt-24 border-b border-border-subtle">
                    <Container>
                        <div className="mx-auto max-w-2xl text-center">
                            <Eyebrow>Schedule a Conversation</Eyebrow>
                            <Heading as="h2" className="mt-4">
                                Let's talk about your business.
                            </Heading>
                        </div>
                        <div className="mx-auto mt-12 max-w-2xl">
                            <FormCard data-testid="cn-form-card">
                                {form.success ? (
                                    <FormSuccess
                                        title="Received."
                                        message="Thanks for reaching out — we'll get back to you within one business day to schedule the discovery conversation."
                                        data-testid="cn-form-success"
                                    />
                                ) : (
                                    <form
                                        onSubmit={form.onSubmit}
                                        noValidate
                                        className="grid grid-cols-1 gap-6 sm:grid-cols-2"
                                        data-testid="cn-form"
                                    >
                                        <FormField
                                            label="Full Name"
                                            name="full_name"
                                            required
                                            value={form.values.full_name}
                                            onChange={form.onChange}
                                            error={form.errors.full_name}
                                            data-testid="cn-input-name"
                                        />
                                        <FormField
                                            label="Email"
                                            name="email"
                                            type="email"
                                            required
                                            value={form.values.email}
                                            onChange={form.onChange}
                                            error={form.errors.email}
                                            data-testid="cn-input-email"
                                        />
                                        <FormField
                                            label="Phone (optional)"
                                            name="phone"
                                            type="tel"
                                            value={form.values.phone}
                                            onChange={form.onChange}
                                            data-testid="cn-input-phone"
                                        />
                                        <FormField
                                            label="Company Name"
                                            name="company_name"
                                            required
                                            value={form.values.company_name}
                                            onChange={form.onChange}
                                            error={form.errors.company_name}
                                            data-testid="cn-input-company"
                                        />
                                        <FormSelect
                                            label="Company Size"
                                            name="company_size"
                                            required
                                            value={form.values.company_size}
                                            onChange={form.onChange}
                                            error={form.errors.company_size}
                                            options={[
                                                "1–10",
                                                "11–50",
                                                "51–200",
                                                "200+",
                                            ]}
                                            data-testid="cn-input-size"
                                        />
                                        <FormSelect
                                            label="Area of Interest"
                                            name="area_of_interest"
                                            required
                                            value={form.values.area_of_interest}
                                            onChange={form.onChange}
                                            error={form.errors.area_of_interest}
                                            options={[
                                                "Operational Systems",
                                                "Financial Operations",
                                                "Growth Strategy",
                                                "Organizational Design",
                                                "M&A Advisory",
                                                "Board & Governance",
                                                "Other",
                                            ]}
                                            data-testid="cn-input-area"
                                        />
                                        <div className="sm:col-span-2">
                                            <FormTextarea
                                                label="Brief Description of Needs"
                                                name="description"
                                                required
                                                placeholder="A few sentences on what you're working on and where you'd like help."
                                                value={form.values.description}
                                                onChange={form.onChange}
                                                error={form.errors.description}
                                                rows={5}
                                                data-testid="cn-input-description"
                                            />
                                        </div>
                                        <div className="sm:col-span-2">
                                            <FormSubmit
                                                label="Schedule Conversation"
                                                submitting={form.submitting}
                                                data-testid="cn-submit-btn"
                                            />
                                        </div>
                                    </form>
                                )}
                            </FormCard>
                        </div>
                    </Container>
                </Section>

                <ClosingCTA
                    eyebrow="Hands-On Advisory"
                    title="Strategy that runs through execution."
                    primary={{ label: "Contact Us", to: "/contact" }}
                    secondary={{ label: "Explore Divisions", to: "/divisions" }}
                />
            </div>
        </PageShell>
    );
}
